import { ref, computed, onMounted, watch, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

const SUPPORTED_LOCALES = new Set(["en", "nl"]);

export function parseLangFromQuery(query: Record<string, unknown>): string | undefined {
  const raw = query.lang ?? query.locale;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value === "string" && SUPPORTED_LOCALES.has(value)) {
    return value;
  }
  return undefined;
}

export function buildEmailSetupQuery(email?: string, lang?: string): Record<string, string> {
  const query: Record<string, string> = {};
  if (email) query.email = email;
  if (lang) query.lang = lang;
  return query;
}

export function useEmailSetupRoute(options?: { emailRef?: Ref<string> }) {
  const route = useRoute();
  const router = useRouter();
  const { locale } = useI18n();

  const internalEmail = ref("");
  const emailAddress = options?.emailRef ?? internalEmail;

  const lang = computed(() => parseLangFromQuery(route.query as Record<string, unknown>));

  const routeQuery = computed(() => buildEmailSetupQuery(emailAddress.value || undefined, lang.value));

  function applyLocaleFromRoute() {
    const fromQuery = parseLangFromQuery(route.query as Record<string, unknown>);
    if (fromQuery) {
      locale.value = fromQuery;
    }
  }

  function syncFromRoute() {
    applyLocaleFromRoute();
    const emailParam = route.query.email;
    if (typeof emailParam === "string") {
      emailAddress.value = emailParam;
    }
  }

  function replaceQuery(partial?: { email?: string }) {
    const email = partial?.email !== undefined ? partial.email : emailAddress.value;
    router.replace({
      query: buildEmailSetupQuery(email || undefined, lang.value),
    });
  }

  function pushTutorial(path: string) {
    router.push({
      path,
      query: buildEmailSetupQuery(emailAddress.value || undefined, lang.value),
    });
  }

  onMounted(syncFromRoute);

  watch(
    () => [route.query.lang, route.query.locale, route.query.email],
    () => syncFromRoute(),
  );

  return {
    emailAddress,
    lang,
    routeQuery,
    replaceQuery,
    pushTutorial,
  };
}
